<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SalesTransactionPartyDocumentsAPI from 'dashboard/api/salesTransactionPartyDocuments';
import NucleoWorkOrderPropertyDocuments from './NucleoWorkOrderPropertyDocuments.vue';

const props = defineProps({
  documents: { type: Object, default: () => ({}) },
  title: { type: String, default: '' },
});

const { t } = useI18n();
const notify = useAlert;
const resolvedTitle = computed(
  () => props.title || t('NUCLEO_WORK_ORDERS.DETAIL.DOCUMENTS.TITLE')
);
const partyDocumentsReason = computed(() => {
  if (
    props.documents.party_documents_reason_code ===
    'party_association_unavailable'
  ) {
    return t('NUCLEO_WORK_ORDERS.DETAIL.DOCUMENTS.PARTIES_UNAVAILABLE');
  }

  return t('NUCLEO_WORK_ORDERS.DETAIL.DOCUMENTS.PARTIES_UNAVAILABLE');
});
const documentTypeLabel = document =>
  document.document_types
    ?.map(type => type.name || type.nome || type.nome_tipo)
    .filter(Boolean)
    .join(', ');

const download = async (party, document) => {
  try {
    const response = await SalesTransactionPartyDocumentsAPI.signedUrl(
      props.documents.property_transaction_id,
      party.id,
      document.id
    );
    window.open(response.data.data.url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    notify(
      error?.response?.data?.message ||
        t('NUCLEO_WORK_ORDERS.DETAIL.DOCUMENTS.DOWNLOAD_ERROR')
    );
  }
};
</script>

<template>
  <section
    class="rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
  >
    <header class="border-b border-lais-border-soft px-5 py-4">
      <h2
        class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
      >
        {{ resolvedTitle }}
      </h2>
    </header>
    <div class="space-y-4 p-5">
      <p
        v-if="!documents.party_documents_available"
        class="rounded-lais bg-lais-bg-soft p-3 text-sm text-lais-text-body"
      >
        {{ partyDocumentsReason }}
      </p>
      <section v-if="documents.party_documents_available" class="grid gap-3">
        <h3 class="text-sm font-semibold text-lais-text">
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.DOCUMENTS.PARTIES_TITLE') }}
        </h3>
        <article
          v-for="party in documents.parties"
          :key="party.id"
          class="rounded-lais border border-lais-border-soft p-3"
        >
          <p class="text-sm font-semibold text-lais-text">{{ party.name }}</p>
          <div v-if="party.documents.length" class="mt-3 grid gap-2">
            <button
              v-for="document in party.documents"
              :key="document.id"
              type="button"
              class="flex items-center justify-between gap-3 rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-left text-sm text-lais-link hover:bg-lais-primary-50"
              @click="download(party, document)"
            >
              <span class="min-w-0 truncate">
                <span
                  v-if="documentTypeLabel(document)"
                  class="block text-xs text-lais-text-muted"
                >
                  {{ documentTypeLabel(document) }}
                </span>
                <span class="block truncate">{{ document.filename }}</span>
              </span>
              <span
                class="i-lucide-download size-4 shrink-0"
                aria-hidden="true"
              />
            </button>
          </div>
          <p v-else class="mt-2 text-xs text-lais-text-muted">
            {{ t('NUCLEO_WORK_ORDERS.DETAIL.DOCUMENTS.PARTY_EMPTY') }}
          </p>
        </article>
      </section>
      <NucleoWorkOrderPropertyDocuments
        :documents="documents.property_documents || []"
        :transaction-id="documents.property_transaction_id"
      />
      <p
        v-if="!documents.evidence_files?.length"
        class="text-sm text-lais-text-muted"
      >
        {{ t('NUCLEO_WORK_ORDERS.DETAIL.DOCUMENTS.EMPTY') }}
      </p>
      <a
        v-for="file in documents.evidence_files"
        :key="file.id"
        :href="file.url"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center justify-between gap-3 rounded-lais border border-lais-border p-3 text-sm text-lais-link hover:bg-lais-primary-50"
      >
        <span class="min-w-0 truncate">{{ file.filename }}</span>
        <span class="shrink-0 text-xs text-lais-text-muted">{{
          file.content_type
        }}</span>
      </a>
    </div>
  </section>
</template>
